﻿<script setup lang="ts">
import {computed, ref, watch} from 'vue';

import {storeToRefs} from 'pinia';
import type {PartInfo, TranspositionResult} from '@/generated';
import {useNotificationStore} from '@/stores/notifications';
import {Icon} from '@iconify/vue';
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import Collapsible from "@/components/generic/Collapsible.vue";
import AnalysisSummary from "@/components/AnalysisSummary.vue";
import IntervalSelect from "@/components/IntervalSelect.vue";
import TransposePartSelector from "@/components/transposition/TransposePartSelector.vue";
import {stableBySignature} from "@/stores/historyMemo";
import {rankSuggestions, suggestionSignature} from "@/components/transposition/suggestionRanking";

const notificationStore = useNotificationStore();

const uploadStore = useMusicUploadStore();
const conversionStore = uploadStore

const {
  currentFileId,
  lineageId,
  analysisResult,
  isAnalyzing,
  transpositionSuggestions,
  modeChangeSuggestions,
  transpositionSuggestionsError,
  isFindingTranspositions,
} = storeToRefs(conversionStore);


const showAllSuggestions = ref(false);
// Sortierung + Signatur als reine Helfer (suggestionRanking.ts, dort begründet und getestet);
// oldValue-Stabilität über Signaturvergleich statt des früheren Längenvergleichs.
const suggestedTranspositions = computed<TranspositionResult[]>(stableBySignature(
    () => transpositionSuggestions.value,
    suggestionSignature,
));
const rankedSuggestions = computed<TranspositionResult[]>(stableBySignature(
    () => rankSuggestions(suggestedTranspositions.value),
    suggestionSignature,
));

const VISIBLE_SUGGESTIONS = 4
const visibleSuggestions = computed(() =>
    showAllSuggestions.value ? rankedSuggestions.value : rankedSuggestions.value.filter(a => a.semitones != 0).slice(0, VISIBLE_SUGGESTIONS)
);

const suggestedTransposition = computed<TranspositionResult | null>(() => {
  return rankedSuggestions.value[0] ?? null;
})

// Variantwechsel (Dur↔Moll): eigene Liste + eigenes v-for – `semitones` ist nur je Liste
// eindeutig (:key-Kollision mit der chromatischen Liste wäre sonst möglich). Kein
// `semitones != 0`-Filter: semitones 0 = reiner Variantwechsel ist der Hauptvorschlag.
const VISIBLE_MODE_CHANGE_SUGGESTIONS = 3;
const rankedModeChangeSuggestions = computed<TranspositionResult[]>(stableBySignature(
    () => rankSuggestions(modeChangeSuggestions.value),
    suggestionSignature,
));
const visibleModeChangeSuggestions = computed(() =>
    rankedModeChangeSuggestions.value.slice(0, VISIBLE_MODE_CHANGE_SUGGESTIONS)
);
const modeChangeBadge = computed(() =>
    rankedModeChangeSuggestions.value[0]?.target_mode === 'major' ? '→ Dur' : '→ Moll'
);
const transposeSemitones = ref<number>(0);
const transpositionStatus = ref('');
const showStatus = ref(false);

// Lokale, transpositions-eigene Part-Auswahl (leer = ganzer Score). Bewusst NICHT der
// settings-Store (selected_parts = Convert-Scope, siehe watch unten).
const transposePartIndices = ref<number[]>([]);
const availableParts = computed<PartInfo[]>(stableBySignature(
    () => analysisResult.value?.parts ?? [],
    (items) => items.map((p) => `${p.index}|${p.name}|${p.clef ?? ''}`).join(';'),
));
const isPartialSelection = computed<boolean>(() => transposePartIndices.value.length > 0);

// Bei Teil-Transposition (einzelne Stimmen) nur tonart-ERHALTENDE Optionen anbieten = Oktaven.
// (Backend-Vorschläge sind überwiegend tonart-ändernd und beziehen sich auf den ganzen Score.)
const OCTAVE_OPTIONS: ReadonlyArray<{ semitones: number; label: string }> = [
  {semitones: -24, label: '2 Oktaven tiefer'},
  {semitones: -12, label: '1 Oktave tiefer'},
  {semitones: 12, label: '1 Oktave höher'},
  {semitones: 24, label: '2 Oktaven höher'},
];

// Bei echtem Dateiwechsel (neue lineageId) ist die alte Index-Auswahl ungültig → zurücksetzen.
// lineageId bleibt über Transpose/Edit/Shift stabil (auch über applyPartTransposition), sodass
// „denselben Part erneut transponieren" nach der Re-Analyse erhalten bleibt; sie ändert sich nur
// bei frischem Upload/Reuse/Movement-Wechsel. (Part-*Anzahl* genügt nicht: Datei A→B mit je 2
// Parts ließe die Auswahl stehen, obwohl Index 1 in B eine andere Stimme sein kann.)
watch(lineageId, () => {
  transposePartIndices.value = [];
});

watch(() => suggestedTransposition.value?.semitones, (sugg) => {
  if (sugg && transposeSemitones.value === 0) {
    transposeSemitones.value = sugg;
  }
}, {immediate: true});

// KEIN Watcher auf `analysisResult.parts`, der `selected_parts` setzt: das macht der Upload-Store
// in `applyAnalysisResult` nach jeder Analyse. Ein `{immediate:true}`-Duplikat hier feuerte bei
// jedem Remount (App.vue rendert die Haupt-Tabs mit v-if) und setzte eine vom Nutzer geänderte
// Part-Auswahl beim Tab-Wechsel still auf „alle" zurück.

function handleTransposeStatus(message: string) {
  transpositionStatus.value = message;
  showStatus.value = !!message;
}


const defaultTransposeSuggestion = computed<TranspositionResult | null>(() => {
  const suggestions = rankedSuggestions.value
  if (!suggestions || !suggestions.length) return null;
  return suggestions[0] as TranspositionResult;
})


async function applyTranspositionInternal(suggestion: TranspositionResult, successMessage: string) {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }
  const result = await uploadStore.applyTransposition({
    ...suggestion,
    interval: suggestion.interval.toString(),
  } as TranspositionResult);

  if (!result) {
    return;
  }

  notificationStore.notify(successMessage, 'success', 2000);
}

async function applyTranspose() {
  // Ist eine Stimme ausgewählt → nur diese transponieren (kein Ganz-Score-Zähler-Bump).
  if (transposePartIndices.value.length) {
    if (!currentFileId.value) {
      notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
      return;
    }
    const result = await uploadStore.applyPartTransposition(
        [...transposePartIndices.value],
        transposeSemitones.value,
    );
    if (result) {
      notificationStore.notify('Transposition auf ausgewählte Stimme(n) angewendet.', 'success', 2000);
    }
    handleTransposeStatus('Transponieren abgeschlossen.');
    return;
  }

  await applyTranspositionInternal(
      {semitones: transposeSemitones.value, interval: transposeSemitones.value.toString(), target_key: ''} as TranspositionResult,
      'Transposition angewendet.',
  );


  handleTransposeStatus('Transponieren abgeschlossen.');
}

async function applyModeChangeInternal(suggestion: TranspositionResult) {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }
  const result = await uploadStore.applyModeChange(suggestion);
  if (!result) return;
  notificationStore.notify(`Variantwechsel nach ${suggestion.target_key} angewendet.`, 'success', 2000);
  handleTransposeStatus('Variantwechsel abgeschlossen.');
}

// Oktav-Vorschlag bei Teilauswahl: verschiebt nur die gewählten Stimmen (gleiche Tonart).
async function applyOctaveToSelection(semitones: number) {
  if (!currentFileId.value || !transposePartIndices.value.length) {
    return;
  }
  const result = await uploadStore.applyPartTransposition([...transposePartIndices.value], semitones);
  if (result) {
    notificationStore.notify('Ausgewählte Stimme(n) um Oktave verschoben.', 'success', 2000);
  }
  handleTransposeStatus('Transponieren abgeschlossen.');
}

</script>

<template>
  <div class="max-w-full flex flex-col flex-auto">
  <!-- Status Badge (Titel liefert der umschließende Tab) -->

  <div v-if="showStatus" class="mb-4 flex justify-end">
    <div class="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100 animate-fade-in">
      {{ transpositionStatus }}
    </div>
  </div>

  <!-- Analyse-Dashboard (Tonart / Taktart / Spielbarkeit + unspielbare Noten) -->
  <AnalysisSummary :analysis="analysisResult" :is-analyzing="isAnalyzing" class="mb-6 flex flex-row flex-auto min-w-0"/>

  <!-- Suggestions List: Skeleton während Analyse -->
  <div v-if="isFindingTranspositions" class="mb-6 space-y-2">
    <div class="flex items-center justify-between mb-3">
      <p class="text-sm font-semibold text-gray-700">Vorschläge</p>
      <Icon icon="fa6-solid:spinner" class="animate-spin text-gray-400 text-xs"/>
    </div>
    <div v-for="i in 3" :key="i" class="h-14 w-full animate-pulse rounded-xl bg-gray-100"/>
  </div>

  <div
      v-else-if="transpositionSuggestionsError"
      class="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
  >
    {{ transpositionSuggestionsError }}
  </div>

  <!-- Teil-Transposition: nur tonart-ERHALTENDE Optionen (Oktaven); Klick wirkt nur auf die Auswahl -->
  <div v-else-if="isPartialSelection" class="mb-6">
    <p class="text-sm font-semibold text-gray-700 mb-3">Gleiche Tonart (Oktave)</p>
    <div class="grid grid-cols-2 gap-2">
      <button
          v-for="opt in OCTAVE_OPTIONS"
          :key="opt.semitones"
          type="button"
          class="flex items-center justify-between p-2 rounded-xl border border-gray-200 bg-teal-400/10 hover:border-blue-300 hover:shadow-md transition-all text-left"
          @click="applyOctaveToSelection(opt.semitones)"
      >
        <span class="font-medium text-gray-800">{{ opt.label }}</span>
        <span class="text-xs text-gray-400 bg-gray-100 px-1.5 rounded">{{ opt.semitones > 0 ? '+' : '' }}{{ opt.semitones }} HT</span>
      </button>
    </div>
  </div>

  <!-- Suggestions List -->
  <div v-else-if="suggestedTranspositions?.length" class="mb-6">
  <div class="flex items-center justify-between mb-3">
    <Collapsible isStatic variant="plain" @click.stop.prevent="showAllSuggestions = !showAllSuggestions">
      <template #header>
        <p class="text-sm font-semibold text-gray-700">Vorschläge</p>

      </template>
      <template #headerRight>
        <button
            v-if="suggestedTranspositions.length > VISIBLE_SUGGESTIONS" type="button"
            class="text-xs text-blue-600 hover:underline bg-gray-100 px-2 py-0.5 rounded-full"
            @click.stop.prevent="showAllSuggestions = !showAllSuggestions"
        >
          {{ showAllSuggestions ? 'Weniger' : `Alle ${suggestedTranspositions.length}` }}
        </button>
        <span v-else class="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{{ suggestedTranspositions.length }} Optionen</span>
      </template>

      <template #default>
        <div class="flex  justify-items-stretch align-items justify-around w-full gap-2 flex-wrap space-y-2 max-h-128 overflow-y-auto pr-1 custom-scrollbar">
          <!-- :key/Vergleich über semitones – target_key ist bei Oktav-Paaren (+7/-5 → beide "G") nicht eindeutig -->
          <button
              v-for="suggestion in (visibleSuggestions as TranspositionResult[])"
              :key="suggestion.semitones"
              type="button"
              class="group flex grow-1 justify-self-stretch h-full items-center justify-between p-1 rounded-xl rounded-l-none border transition-all duration-200 text-left"
              :class="[
              suggestion.semitones === defaultTransposeSuggestion?.semitones
                ? 'bg-blue-50/50 border-blue-200 shadow-sm'
                : 'bg-teal-400/10 border-gray-200 hover:border-blue-300 hover:shadow-md',
              suggestion.analysis.unmappable_count === 0 ? 'border-l-4 border-l-green-400' : 'border-l-3 border-l-orange-200/80'
            ]"
              @click="()=>applyTranspositionInternal(suggestion, 'Transposition zu ' + suggestion?.interval + ' angewendet.')"
          >
            <div class="flex items-center gap-4">
              <div class="flex flex-col items-center justify-center w-10 h-9 rounded-lg bg-white/80 border-2 border-gray-200 text-gray-800 font-bold border border-gray-200 group-hover:bg-blue-500 group-hover:text-white group-hover:border-blue-500 transition-colors text-sm">
                {{ suggestion.interval }}
              </div>

              <div>
                <div class="flex items-center gap-2">
                  <span class="font-bold text-gray-800">{{ suggestion.target_key }}</span>
                  <!--                  <span class="text-gray-400 text-sm">{{ suggestion.octave }}</span>-->
                  <span class="text-sm text-gray-400 bg-gray-100 px-1.5 rounded">{{ suggestion.semitones > 0 ? '+' : '' }}{{ suggestion.semitones }} HT</span>
                </div>

                <!-- Issues Preview -->
                <div v-if="suggestion.analysis.unmappable_count > 0" class="flex flex-wrap gap-1 mt-1.5">
                <span
                    v-for="note in suggestion.analysis.unmappable.slice(0, 3)" :key="note"
                    class="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded border border-red-100"
                >
                  {{ note }}
                </span>
                  <span v-if="suggestion.analysis.unmappable.length > 3" class="text-[10px] text-gray-400">+{{ suggestion.analysis.unmappable.length - 3 }}</span>
                </div>
                <div v-else class="text-xs text-green-600 mt-0.5 flex items-center">
                  <Icon icon="fa6-solid:check" class="mr-1 text-[10px]"/>
                  Optimal
                </div>
              </div>
            </div>

            <!-- Percentage Score -->
            <div v-if="suggestion.analysis.percentage" class="text-right">
              <div
                  class="text-sm font-bold"
                  :class="suggestion.analysis.percentage > 90 ? 'text-green-600' : 'text-orange-500'"
              >
                {{ suggestion.analysis.percentage.toFixed(0) }}%
              </div>
              <div class="text-[10px] text-gray-400">Spielbar</div>
            </div>
          </button>
        </div>

      </template>
    </Collapsible>

  </div>

  </div>

  <!-- Variantwechsel (Dur ↔ Moll): eigener Abschnitt mit eigenem v-for (:key nur je Liste eindeutig) -->
  <div
      v-if="!isFindingTranspositions && !isPartialSelection && visibleModeChangeSuggestions.length"
      class="mb-6"
  >
    <div class="flex items-center justify-between mb-3">
      <p class="text-sm font-semibold text-gray-700">Variantwechsel (Dur ↔ Moll)</p>
      <span class="text-xs text-sky-700 bg-sky-50 border border-sky-100 px-2 py-0.5 rounded-full">{{ modeChangeBadge }}</span>
    </div>
    <div class="flex justify-items-stretch justify-around w-full gap-2 flex-wrap space-y-2">
      <button
          v-for="suggestion in visibleModeChangeSuggestions"
          :key="suggestion.semitones"
          type="button"
          class="group flex grow-1 justify-self-stretch h-full items-center justify-between p-1 rounded-xl rounded-l-none border transition-all duration-200 text-left bg-sky-400/10 border-gray-200 hover:border-sky-300 hover:shadow-md"
          :class="suggestion.analysis.unmappable_count === 0 ? 'border-l-4 border-l-green-400' : 'border-l-3 border-l-orange-200/80'"
          @click="() => applyModeChangeInternal(suggestion)"
      >
        <div class="flex items-center gap-4">
          <div class="flex flex-col items-center justify-center w-10 h-9 rounded-lg bg-white/80 border-2 border-gray-200 text-gray-800 font-bold group-hover:bg-sky-500 group-hover:text-white group-hover:border-sky-500 transition-colors text-xs">
            {{ suggestion.target_mode === 'major' ? 'Dur' : 'Moll' }}
          </div>

          <div>
            <div class="flex items-center gap-2">
              <span class="font-bold text-gray-800">{{ suggestion.target_key }}</span>
              <span class="text-sm text-gray-400 bg-gray-100 px-1.5 rounded">{{ suggestion.semitones > 0 ? '+' : '' }}{{ suggestion.semitones }} HT</span>
            </div>

            <div v-if="suggestion.analysis.unmappable_count > 0" class="flex flex-wrap gap-1 mt-1.5">
              <span
                  v-for="note in suggestion.analysis.unmappable.slice(0, 3)" :key="note"
                  class="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded border border-red-100"
              >
                {{ note }}
              </span>
              <span v-if="suggestion.analysis.unmappable.length > 3" class="text-[10px] text-gray-400">+{{ suggestion.analysis.unmappable.length - 3 }}</span>
            </div>
            <div v-else class="text-xs text-green-600 mt-0.5 flex items-center">
              <Icon icon="fa6-solid:check" class="mr-1 text-[10px]"/>
              Optimal
            </div>
          </div>
        </div>

        <div v-if="suggestion.analysis.percentage" class="text-right">
          <div
              class="text-sm font-bold"
              :class="suggestion.analysis.percentage > 90 ? 'text-green-600' : 'text-orange-500'"
          >
            {{ suggestion.analysis.percentage.toFixed(0) }}%
          </div>
          <div class="text-[10px] text-gray-400">Spielbar</div>
        </div>
      </button>
    </div>
  </div>

  <!-- Manual Controls -->
  <div class="flex flex-col gap-3 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
    <!-- Optionale Beschränkung auf einzelne Stimmen (nur bei mehrstimmigen Stücken) -->
    <TransposePartSelector :parts="availableParts" v-model:selected="transposePartIndices"/>

    <div class="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
      <div class="w-full sm:flex-1">
        <IntervalSelect
            v-model:semitones="transposeSemitones"
            label="Manuelles Intervall"
        />
      </div>

      <div class="flex gap-2 w-full sm:w-auto">
        <button
            id="applyTranspose"
            class="btn btn-primary flex-1 sm:flex-none"
            type="button"
            :disabled="transposeSemitones == 0"
            @click="applyTranspose"
        >
          {{ transposePartIndices.length ? 'Stimme(n) transponieren' : 'Transponieren' }}
        </button>
      </div>
    </div>
  </div>
  </div>
</template>
